import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';
import MetricCard from './components/MetricCard';
import ActivityFeedItem from './components/ActivityFeedItem';
import ResourceUtilizationChart from './components/ResourceUtilizationChart';
import QuickActionCard from './components/QuickActionCard';
import ProjectTimelineChart from './components/ProjectTimelineChart';
import ConversionFunnelChart from './components/ConversionFunnelChart';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { createRealtimeSubscription, RealtimeErrorType } from '../../utils/realtimeErrorHandler';
import {
  calculateDashboardMetrics,
  getResourceUtilizationByCategory,
  getConversionFunnelData,
  getActiveProjectsTimeline,
  fetchRecentActivities,
  getActiveUserCount,
  getProspectSummary } from
'../../services/dashboardService';
import WelcomeSetupCard from './components/WelcomeSetupCard';

const Dashboard = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [projects, setProjects] = useState([]);
  const [bids, setBids] = useState([]);
  const [resources, setResources] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [activeUserCount, setActiveUserCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [realtimeError, setRealtimeError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [timeRange, setTimeRange] = useState('month');
  const [showWelcomeCard, setShowWelcomeCard] = useState(false);
  const [prospectSummary, setProspectSummary] = useState(null);

  // Helper: get start date for the selected time range
  const getTimeRangeStart = (range) => {
    const now = new Date();
    switch (range) {
      case 'week':
        return new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
      case 'month':
        return new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      case 'quarter':
        return new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
      case 'year':
        return new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      default:
        return new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    }
  };

  // Filter data by time range
  const rangeStart = getTimeRangeStart(timeRange);

  const filteredProjects = projects?.filter((p) => {
    const date = new Date(p?.created_at || p?.start_date);
    return date >= rangeStart;
  });

  const filteredBids = bids?.filter((b) => {
    const date = new Date(b?.created_at);
    return date >= rangeStart;
  });

  const filteredActivities = activities?.filter((a) => {
    return a?.timestamp >= rangeStart;
  });

  // Error handler for real-time subscriptions
  const handleRealtimeError = (error) => {
    setRealtimeError(error);
    setIsRetrying(error?.willRetry || false);

    if (!error?.willRetry && error?.type !== RealtimeErrorType?.CONNECTION_FAILED) {
      setTimeout(() => setRealtimeError(null), 5000);
    }
  };

  // Real-time subscription for projects, bids, and resources
  useEffect(() => {
    // Check if welcome card should be shown
    if (userProfile?.id) {
      const dismissed = localStorage.getItem(`welcome_dismissed_${userProfile?.id}`) === 'true';
      const isNew = !userProfile?.profile_completed;
      setShowWelcomeCard(!dismissed && isNew);
    }

    // Initial data fetch
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const { data: projectsData } = await supabase?.from('projects')?.select('*');
        const { data: bidsData } = await supabase?.from('bids')?.select('*');
        const { data: resourcesData } = await supabase?.from('resources')?.select('*');
        const { data: assignmentsData } = await supabase?.from('resource_assignments')?.select('*');

        setProjects(projectsData || []);
        setBids(bidsData || []);
        setResources(resourcesData || []);
        setAssignments(assignmentsData || []);

        // Fetch activities and user count
        const activitiesData = await fetchRecentActivities();
        setActivities(activitiesData);

        const userCount = await getActiveUserCount();
        setActiveUserCount(userCount);

        // Fetch prospect summary
        const prospectData = await getProspectSummary();
        setProspectSummary(prospectData);
      } catch (err) {
        setError(err?.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();

    // Real-time subscription for projects with error handling
    const { cleanup: cleanupProjects } = createRealtimeSubscription({
      supabase,
      channelName: 'dashboard_projects',
      tableName: 'projects',
      onDataChange: async () => {
        const { data } = await supabase?.from('projects')?.select('*');
        setProjects(data || []);
        // Refresh activities when projects change
        const activitiesData = await fetchRecentActivities();
        setActivities(activitiesData);
      },
      onError: handleRealtimeError
    });

    // Real-time subscription for bids with error handling
    const { cleanup: cleanupBids } = createRealtimeSubscription({
      supabase,
      channelName: 'dashboard_bids',
      tableName: 'bids',
      onDataChange: async () => {
        const { data } = await supabase?.from('bids')?.select('*');
        setBids(data || []);
        // Refresh activities when bids change
        const activitiesData = await fetchRecentActivities();
        setActivities(activitiesData);
      },
      onError: handleRealtimeError
    });

    // Real-time subscription for resources with error handling
    const { cleanup: cleanupResources } = createRealtimeSubscription({
      supabase,
      channelName: 'dashboard_resources',
      tableName: 'resources',
      onDataChange: async () => {
        const { data } = await supabase?.from('resources')?.select('*');
        setResources(data || []);
      },
      onError: handleRealtimeError
    });

    // Real-time subscription for resource assignments
    const { cleanup: cleanupAssignments } = createRealtimeSubscription({
      supabase,
      channelName: 'dashboard_assignments',
      tableName: 'resource_assignments',
      onDataChange: async () => {
        const { data } = await supabase?.from('resource_assignments')?.select('*');
        setAssignments(data || []);
      },
      onError: handleRealtimeError
    });

    // Cleanup subscriptions
    return () => {
      cleanupProjects();
      cleanupBids();
      cleanupResources();
      cleanupAssignments();
    };
  }, []);

  // Calculate metrics from real data
  const metricsData = calculateDashboardMetrics(filteredProjects, filteredBids, resources);
  const metrics = [
  {
    id: 1,
    title: "Active Projects",
    value: metricsData?.activeProjects?.value,
    subtitle: metricsData?.activeProjects?.subtitle,
    icon: "Briefcase",
    trend: "up",
    trendValue: "+12%",
    color: "primary"
  },
  {
    id: 2,
    title: "Bid Conversion Rate",
    value: metricsData?.conversionRate?.value,
    subtitle: metricsData?.conversionRate?.subtitle,
    icon: "TrendingUp",
    trend: "up",
    trendValue: "+5.2%",
    color: "success"
  },
  {
    id: 3,
    title: "Resource Utilization",
    value: metricsData?.utilization?.value,
    subtitle: metricsData?.utilization?.subtitle,
    icon: "Users",
    trend: "down",
    trendValue: "-3%",
    color: "warning"
  },
  {
    id: 4,
    title: "Total Bids",
    value: metricsData?.totalBids?.value,
    subtitle: metricsData?.totalBids?.subtitle,
    icon: "FileText",
    trend: "up",
    trendValue: "+18",
    color: "accent"
  }];


  // Get resource utilization by category
  const resourceUtilization = getResourceUtilizationByCategory(resources, assignments);

  // Get conversion funnel data
  const conversionFunnelData = getConversionFunnelData(filteredBids, filteredProjects);

  // Get active projects for timeline
  const activeProjects = getActiveProjectsTimeline(filteredProjects);

  const quickActions = [
  {
    id: 1,
    label: "Create New Bid",
    icon: "FilePlus",
    path: "/bid-management",
    variant: "default"
  },
  {
    id: 2,
    label: "Add Project",
    icon: "FolderPlus",
    path: "/project-management",
    variant: "outline"
  },
  {
    id: 3,
    label: "Assign Resources",
    icon: "UserPlus",
    path: "/resource-management",
    variant: "outline"
  },
  {
    id: 4,
    label: "Manage Customers",
    icon: "Users",
    path: "/customer-management",
    variant: "outline"
  }];


  return (
    <MainLayout>
      <div className="space-y-6 mt-[26px]">
        {/* Real-time Error Alert */}
        {realtimeError &&
        <div className={`rounded-lg border p-4 flex items-start gap-3 ${
        realtimeError?.type === RealtimeErrorType?.CONNECTION_FAILED ?
        'bg-red-50 border-red-200 text-red-800' : 'bg-yellow-50 border-yellow-200 text-yellow-800'}`
        }>
            <Icon
            name={isRetrying ? 'RefreshCw' : 'AlertTriangle'}
            size={20}
            className={isRetrying ? 'animate-spin' : ''} />
          
            <div className="flex-1">
              <p className="font-medium text-sm">
                {realtimeError?.message}
              </p>
              {realtimeError?.attemptCount &&
            <p className="text-xs mt-1 opacity-80">
                  Retry attempt {realtimeError?.attemptCount}/3
                </p>
            }
            </div>
            {!isRetrying && realtimeError?.type === RealtimeErrorType?.CONNECTION_FAILED &&
          <button
            onClick={() => window.location?.reload()}
            className="text-sm font-medium underline hover:no-underline">
            
                Refresh Page
              </button>
          }
            {!isRetrying && realtimeError?.type !== RealtimeErrorType?.CONNECTION_FAILED &&
          <button
            onClick={() => setRealtimeError(null)}
            className="text-sm font-medium">
            
                <Icon name="X" size={16} />
              </button>
          }
          </div>
        }

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-2">Dashboard</h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Welcome back! Here's what's happening with your construction projects today.
            </p>
          </div>
          <div className="flex items-center gap-2 bg-card border border-border rounded-lg p-1">
            {['week', 'month', 'quarter', 'year']?.map((range) =>
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-250 ${
              timeRange === range ?
              'bg-accent text-accent-foreground' :
              'text-muted-foreground hover:text-foreground'}`
              }>
              
                {range?.charAt(0)?.toUpperCase() + range?.slice(1)}
              </button>
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading &&
        <div className="flex items-center justify-center py-12">
            <Icon name="RefreshCw" size={32} className="animate-spin text-muted-foreground" />
          </div>
        }

        {/* Error State */}
        {error &&
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
            <p className="font-medium">Error loading dashboard data</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        }

        {/* Dashboard Content */}
        {!loading && !error &&
        <>
            {/* Welcome Setup Card for new users */}
            {showWelcomeCard &&
          <WelcomeSetupCard onDismiss={() => setShowWelcomeCard(false)} />
          }

            {/* Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {metrics?.map((metric) =>
            <MetricCard key={metric?.id} {...metric} />
            )}
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
              {/* Left Column - Activity Feed & Conversion Funnel */}
              <div className="lg:col-span-2 space-y-6 md:space-y-8">
                {/* Recent Activity */}
                <div className="bg-card rounded-xl border border-border overflow-hidden">
                  <div className="p-4 md:p-6 border-b border-border">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg md:text-xl font-semibold text-foreground">Recent Activity</h2>
                      <Icon name="Activity" size={20} className="text-muted-foreground" />
                    </div>
                  </div>
                  <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
                    {filteredActivities?.length > 0 ?
                  filteredActivities?.map((activity) =>
                  <ActivityFeedItem key={activity?.id} activity={activity} />
                  ) :

                  <div className="p-8 text-center text-muted-foreground">
                        <Icon name="Activity" size={48} className="mx-auto mb-2 opacity-20" />
                        <p>No recent activities</p>
                      </div>
                  }
                  </div>
                </div>

                {/* Conversion Funnel */}
                <ConversionFunnelChart data={conversionFunnelData} />

                {/* Project Timeline */}
                {activeProjects?.length > 0 &&
              <ProjectTimelineChart projects={activeProjects} />
              }
              </div>

              {/* Right Column - Resource Utilization & Quick Actions */}
              <div className="space-y-6 md:space-y-8">
                {resourceUtilization?.length > 0 &&
              <ResourceUtilizationChart resources={resourceUtilization} />
              }
                <QuickActionCard actions={quickActions} />

                {/* System Status */}
                <div className="bg-card rounded-xl p-4 md:p-6 border border-border">
                  <h3 className="text-lg md:text-xl font-semibold text-foreground mb-4">System Status</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-success/10 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Icon name="CheckCircle" size={18} className="text-success" />
                        <span className="text-sm font-medium text-foreground">All Systems Operational</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div className="flex items-center gap-2">
                        <Icon name="Database" size={18} className="text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Last backup</span>
                      </div>
                      <span className="text-xs text-muted-foreground">2 hours ago</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div className="flex items-center gap-2">
                        <Icon name="Users" size={18} className="text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Active users</span>
                      </div>
                      <span className="text-xs font-semibold text-foreground">{activeUserCount}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        }
      </div>
    </MainLayout>);


};

export default Dashboard;